/**
 * TracksOverview — DRR-309 Phase 2, Phase 8
 *
 * Multi-track progress visualization for parallel workflows.
 * Each track shows its display name (from fragment config), status, and progress.
 *
 * Phase 8: Responsive breakpoints for mobile/tablet/desktop
 */

import React from 'react';
import {
  Stack,
  Group,
  Text,
  Progress,
  Paper,
  ThemeIcon,
  Badge,
  Tooltip,
  Card,
  Box,
  SimpleGrid,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconCheck,
  IconPlayerPlay,
  IconPlayerPause,
  IconClock,
  IconX,
  IconGitBranch,
} from '@tabler/icons-react';
import type { TracksOverviewProps, JourneyTrack } from '../types';

const TRACK_STATUS_CONFIG: Record<
  string,
  { color: string; icon: React.ReactNode }
> = {
  not_started: { color: 'gray', icon: <IconClock size={14} /> },
  running: { color: 'blue', icon: <IconPlayerPlay size={14} /> },
  paused: { color: 'yellow', icon: <IconPlayerPause size={14} /> },
  completed: { color: 'green', icon: <IconCheck size={14} /> },
  failed: { color: 'red', icon: <IconX size={14} /> },
};

interface TrackItemProps {
  track: JourneyTrack;
  onClick?: () => void;
  compact?: boolean;
}

function TrackItem({ track, onClick, compact = false }: TrackItemProps) {
  const statusConfig = TRACK_STATUS_CONFIG[track.status.toLowerCase()] ?? {
    color: 'gray',
    icon: <IconClock size={14} />,
  };

  const progressColor =
    track.status === 'completed'
      ? 'green'
      : track.status === 'failed'
        ? 'red'
        : track.progressPercent > 50
          ? 'blue'
          : 'gray';

  if (compact) {
    return (
      <Tooltip label={`${track.displayName}: ${Math.round(track.progressPercent)}%`}>
        <Group gap={4} style={{ cursor: onClick ? 'pointer' : undefined }} onClick={onClick}>
          <ThemeIcon size="xs" variant="light" color={statusConfig.color}>
            {statusConfig.icon}
          </ThemeIcon>
          <Progress
            value={track.progressPercent}
            color={progressColor}
            size="sm"
            w={60}
            radius="xl"
          />
        </Group>
      </Tooltip>
    );
  }

  return (
    <Card
      padding="sm"
      withBorder
      style={{ cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon size="md" variant="light" color={statusConfig.color}>
            {statusConfig.icon}
          </ThemeIcon>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text size="sm" fw={500} truncate>
              {track.displayName}
            </Text>
            {track.currentState && (
              <Text size="xs" c="dimmed" truncate>
                {track.currentState.replace(/_/g, ' ')}
              </Text>
            )}
          </div>
        </Group>

        <Group gap="xs" wrap="nowrap">
          {track.isPaused && (
            <Badge size="xs" color="yellow" variant="light">
              Paused
            </Badge>
          )}
          <Progress
            value={track.progressPercent}
            color={progressColor}
            size="sm"
            w={80}
            radius="xl"
          />
          <Text size="xs" fw={500} w={35} ta="right">
            {Math.round(track.progressPercent)}%
          </Text>
        </Group>
      </Group>
    </Card>
  );
}

export function TracksOverview({
  tracks,
  onTrackClick,
  compact = false,
}: TracksOverviewProps) {
  // Phase 8: Responsive breakpoints
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  if (tracks.length === 0) return null;

  // Sort tracks: running first, then by progress desc
  const sortedTracks = [...tracks].sort((a, b) => {
    if (a.status === 'running' && b.status !== 'running') return -1;
    if (a.status !== 'running' && b.status === 'running') return 1;
    return b.progressPercent - a.progressPercent;
  });

  // Calculate overall track progress
  const completedTracks = tracks.filter((t) => t.status === 'completed').length;

  if (compact) {
    return (
      <Group gap="xs">
        {sortedTracks.slice(0, isMobile ? 2 : 3).map((track) => (
          <TrackItem
            key={track.trackId}
            track={track}
            onClick={onTrackClick ? () => onTrackClick(track.trackId) : undefined}
            compact
          />
        ))}
        {tracks.length > (isMobile ? 2 : 3) && (
          <Badge size="xs" variant="light">
            +{tracks.length - (isMobile ? 2 : 3)}
          </Badge>
        )}
      </Group>
    );
  }

  // Mobile layout: stacked with minimal info
  if (isMobile) {
    return (
      <Paper p="sm" withBorder>
        <Stack gap="sm">
          {/* Header */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon size="sm" variant="light" color="violet">
                <IconGitBranch size={14} />
              </ThemeIcon>
              <Text size="xs" fw={500}>
                Tracks
              </Text>
            </Group>
            <Badge size="xs" variant="light" color="violet">
              {completedTracks}/{tracks.length}
            </Badge>
          </Group>

          {/* Track list - show first 3 on mobile */}
          <Stack gap={4}>
            {sortedTracks.slice(0, 3).map((track) => (
              <TrackItem
                key={track.trackId}
                track={track}
                onClick={onTrackClick ? () => onTrackClick(track.trackId) : undefined}
                compact
              />
            ))}
            {tracks.length > 3 && (
              <Text size="xs" c="dimmed" ta="center">
                +{tracks.length - 3} more tracks
              </Text>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  // Tablet layout: 2-column grid
  if (isTablet) {
    return (
      <Paper p="sm" withBorder>
        <Stack gap="sm">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon size="md" variant="light" color="violet">
                <IconGitBranch size={16} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                Parallel Tracks
              </Text>
            </Group>
            <Badge variant="light" color="violet">
              {completedTracks}/{tracks.length} complete
            </Badge>
          </Group>

          {/* Track grid */}
          <SimpleGrid cols={2} spacing="xs">
            {sortedTracks.map((track) => (
              <TrackItem
                key={track.trackId}
                track={track}
                onClick={onTrackClick ? () => onTrackClick(track.trackId) : undefined}
              />
            ))}
          </SimpleGrid>
        </Stack>
      </Paper>
    );
  }

  // Desktop layout: full
  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="sm">
            <ThemeIcon size="md" variant="light" color="violet">
              <IconGitBranch size={16} />
            </ThemeIcon>
            <Text size="sm" fw={500}>
              Parallel Tracks
            </Text>
          </Group>
          <Badge variant="light" color="violet">
            {completedTracks}/{tracks.length} complete
          </Badge>
        </Group>

        {/* Track list */}
        <Stack gap="xs">
          {sortedTracks.map((track) => (
            <TrackItem
              key={track.trackId}
              track={track}
              onClick={onTrackClick ? () => onTrackClick(track.trackId) : undefined}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default TracksOverview;
